import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * WorkspaceLayout — full-height page shell for workspace screens (T-0140).
 *
 * Mirrors `.rq-page` / `.rq-body` / `.rq-main` in
 * `docs/requirement-library/prototype/selection.css`:
 *
 *   header
 *   banner (optional)
 *   body:  rail | main column (toolbar, scrolling content, footer) | panel
 *
 * The side panel is docked beside the main column when the viewport is
 * at least 1500px wide, and floats over the main column with a shadow
 * below that. The switch is made in JS (`useSidePanelMode`) rather than
 * in CSS alone so children can react to it: a docked panel repeats some
 * row detail, so a grid may drop a column while it is docked. Read the
 * mode with `useWorkspacePanel()`.
 *
 * The layout fills its parent's height; give the parent a height (for
 * example `h-[calc(100vh-56px)]` under the app top bar).
 */

export const SIDE_PANEL_DOCK_MIN_WIDTH = 1500;

export type SidePanelMode = 'docked' | 'floating';

/** Pure rule behind the docked / floating switch. */
export function sidePanelMode(viewportWidth: number): SidePanelMode {
  return viewportWidth >= SIDE_PANEL_DOCK_MIN_WIDTH ? 'docked' : 'floating';
}

/** Tracks the viewport against the dock breakpoint via `matchMedia`. */
export function useSidePanelMode(): SidePanelMode {
  const query = `(min-width: ${SIDE_PANEL_DOCK_MIN_WIDTH}px)`;
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = React.useCallback((): SidePanelMode => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'docked';
    return window.matchMedia(query).matches ? 'docked' : 'floating';
  }, [query]);
  return React.useSyncExternalStore(subscribe, getSnapshot, () => 'docked' as const);
}

interface WorkspacePanelContextValue {
  /** True when a panel is currently rendered. */
  open: boolean;
  mode: SidePanelMode;
}

const WorkspacePanelContext = React.createContext<WorkspacePanelContextValue>({
  open: false,
  mode: 'docked',
});

/** Panel state for children of a WorkspaceLayout. */
export function useWorkspacePanel(): WorkspacePanelContextValue {
  return React.useContext(WorkspacePanelContext);
}

export interface WorkspaceLayoutProps {
  /** Page header: title, meta line, actions, tabs. */
  header?: React.ReactNode;
  /** Optional banner between header and body. See `WorkspaceBanner`. */
  banner?: React.ReactNode;
  /** Left rail, usually a `TreeRail`. */
  rail?: React.ReactNode;
  /** Accessible name for the rail landmark. */
  railLabel?: string;
  /** Toolbar above the scrolling content: search, filters, count. */
  toolbar?: React.ReactNode;
  /** Main content. A `WorkspaceDataGrid` scrolls itself; other content scrolls here. */
  children: React.ReactNode;
  /** Footer hint strip under the content, for example keyboard hints. */
  footer?: React.ReactNode;
  /** Side panel, usually a `SidePanel`. Pass null / undefined when closed. */
  panel?: React.ReactNode;
  /** Floating elements positioned over the main column, such as a `BulkBar`. */
  overlay?: React.ReactNode;
  /**
   * Keep the panel docked beside the main column at every width, for screens
   * whose main column must never be covered (a process map, OQ-V).
   */
  panelMode?: SidePanelMode;
  className?: string;
}

export function WorkspaceLayout({
  header,
  banner,
  rail,
  railLabel = 'Sections',
  toolbar,
  children,
  footer,
  panel,
  overlay,
  panelMode,
  className,
}: WorkspaceLayoutProps) {
  const autoMode = useSidePanelMode();
  const mode = panelMode ?? autoMode;
  const open = panel != null && panel !== false;
  const ctx = React.useMemo(() => ({ open, mode }), [open, mode]);

  return (
    <WorkspacePanelContext.Provider value={ctx}>
      <div className={cn('flex h-full min-h-0 min-w-0 flex-1 flex-col', className)}>
        {header ? <div className="flex-none">{header}</div> : null}
        {banner ? <div className="flex-none px-6 pb-3">{banner}</div> : null}
        <div
          data-panel-mode={open ? mode : undefined}
          className="relative flex min-h-0 flex-1 border-t border-ink-100 bg-paper-0"
        >
          {rail ? (
            <nav
              aria-label={railLabel}
              className="w-[248px] flex-none overflow-y-auto border-r border-ink-100 bg-paper-50 px-2 pb-6 pt-2.5 max-[1280px]:w-[208px]"
            >
              {rail}
            </nav>
          ) : null}
          <div className="relative flex min-w-0 flex-1 flex-col">
            {toolbar ? (
              <div className="flex flex-none flex-wrap items-center gap-2.5 border-b border-ink-100 px-4 py-2.5">
                {toolbar}
              </div>
            ) : null}
            <div className="flex min-h-0 flex-1 flex-col overflow-auto">{children}</div>
            {footer ? (
              <div className="flex flex-none flex-wrap gap-3.5 border-t border-ink-100 bg-paper-50 px-4 py-1.5 text-meta text-ink-500">
                {footer}
              </div>
            ) : null}
            {overlay}
          </div>
          {open ? (
            <div
              data-testid="workspace-panel-slot"
              data-mode={mode}
              className={cn(
                'flex min-h-0 w-[420px] flex-none flex-col border-l border-ink-100 bg-paper-0 max-[1280px]:w-[380px]',
                mode === 'floating' && 'absolute inset-y-0 right-0 z-30 max-w-full shadow-modal',
              )}
            >
              {panel}
            </div>
          ) : null}
        </div>
      </div>
    </WorkspacePanelContext.Provider>
  );
}

WorkspaceLayout.displayName = 'WorkspaceLayout';

export interface WorkspaceHeaderProps {
  title: React.ReactNode;
  /** Meta line under the title. */
  meta?: React.ReactNode;
  /** Right-aligned actions. */
  actions?: React.ReactNode;
  className?: string;
}

/** Standard header row: title + meta on the left, actions on the right. */
export function WorkspaceHeader({ title, meta, actions, className }: WorkspaceHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-end justify-between gap-x-4 gap-y-3 px-6 pb-3.5 pt-5',
        className,
      )}
    >
      <div className="min-w-0">
        <h1 className="text-h1 font-semibold text-ink-900">{title}</h1>
        {meta ? <div className="mt-0.5 text-meta font-medium text-ink-500">{meta}</div> : null}
      </div>
      {actions ? <div className="flex flex-none items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export interface WorkspaceBannerProps {
  children: React.ReactNode;
  /** Right-aligned actions inside the banner. */
  actions?: React.ReactNode;
  className?: string;
}

/** Amber notice strip (prototype `.rq-banner`), for example an open draft. */
export function WorkspaceBanner({ children, actions, className }: WorkspaceBannerProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-3 rounded-card border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-accent-amber-50 px-3.5 py-2.5 text-body-sm text-ink-700',
        className,
      )}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {actions ? <div className="flex flex-none items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** One keyboard hint for the footer strip: `<KeyHint keys={['j','k']}>move</KeyHint>`. */
export function KeyHint({ keys, children }: { keys: string[]; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {keys.map((k) => (
        <kbd key={k} className="kbd">
          {k}
        </kbd>
      ))}
      {children}
    </span>
  );
}
